<template>
  <div
    ref="navContainer"
    class="relative flex h-full grow flex-row overflow-hidden scroll-shadow"
  >
    <TransitionGroup
      :css="false"
      class="flex size-full flex-row"
      tag="div"
      @enter="enter as (el: Element) => void"
      @leave="leave as (el: Element) => void"
      @before-enter="beforeEnter as (el: Element) => void"
      @after-leave="afterLeave as (el: Element) => void"
    >
      <!-- Main menu -->
      <MobileNavPanel
        v-if="!selectedItem"
        :key="'main'"
        class="flex h-full min-w-full shrink-0 flex-col justify-between"
        @click="handleClick"
      >
        <ul class="flex flex-col gap-y-5 overflow-hidden p-4">
          <li
            v-for="item in navigationItems"
            :key="item.id"
            class="flex min-h-[88px] cursor-pointer items-center rounded-lg bg-gray-200 px-4"
            data-testid="mobile-nav-link-main"
            @click.stop="selectItem(item)"
          >
            <template v-if="item.children.length === 0">
              <SFLink
                :target="
                  buildNavigationTreeItemRoute(item)?.openInNew
                    ? '_blank'
                    : '_self'
                "
                :to="buildNavigationTreeItemRoute(item)?.route || ''"
                class="grow"
              >
                <SFNavigationTreeItem
                  :navigation-item="item"
                  :text-color="theme.colors.gray[900]"
                  class="min-h-11 rounded font-sans !text-xl !font-medium !text-gray-600 transition-all supports-hover:hover:bg-[var(--backgroundColor)]"
                  show-navigation-image
                  disabled-link
                />
              </SFLink>
            </template>
            <template v-else>
              <SFNavigationTreeItem
                :navigation-item="item"
                :text-color="theme.colors.gray[900]"
                class="min-h-11 font-sans !text-2xl !font-medium !text-gray-600"
                show-navigation-image
                disabled-link
                role="button"
              />
            </template>
            <SFButton
              v-if="item.children.length"
              aria-hidden="true"
              class="ml-auto !h-auto min-h-11 rounded-md bg-gray-200 !p-3 hover:!bg-gray-200"
              variant="accent"
              @click.stop="selectItem(item)"
            >
              <IconChevronRight class="size-4 text-gray-600" />
            </SFButton>
          </li>
        </ul>

        <SFMobileHeaderUserArea @click-link="handleClick" />
      </MobileNavPanel>

      <!-- Submenu -->
      <MobileNavPanel
        v-if="selectedItem && !selectedSubItem"
        :key="'submenu'"
        class="h-full min-w-full shrink-0"
      >
        <SFMobileNavigationBackButton
          :navigation-item="selectedItem"
          :text-color="theme.colors.gray[900]"
          item-class="font-sans !text-xl !font-medium !text-gray-600"
          @click="backToMain"
        />
        <div class="flex flex-col gap-y-4 p-4" @click="handleClick">
          <template v-for="item in selectedItem.children" :key="item.id">
            <div
              v-if="item.children.length"
              class="flex min-h-[88px] cursor-pointer items-center justify-between rounded-md bg-gray-200 p-4"
              @click="selectSubItem(item)"
              @click.stop
            >
              <SFNavigationTreeItem
                :navigation-item="item"
                :text-color="theme.colors.gray[900]"
                class="flex-1 font-sans !text-xl !font-medium !text-gray-600"
                show-navigation-image
                disabled-link
              />
              <IconChevronRight class="ml-2 size-4 text-gray-600" />
            </div>
            <SFNavigationTreeItem
              v-else
              :navigation-item="item"
              :text-color="theme.colors.gray[900]"
              class="min-h-[88px] w-full justify-center rounded-md bg-gray-200 p-4 font-sans !text-xl !font-medium !text-gray-600"
              show-navigation-image
              @click="$emit('clickLink')"
            />
          </template>
        </div>
      </MobileNavPanel>

      <!-- Subsubmenu -->
      <MobileNavPanel
        v-if="selectedSubItem"
        :key="'subsubmenu'"
        class="h-full min-w-full shrink-0"
      >
        <SFMobileNavigationBackButton
          :navigation-item="selectedSubItem"
          :text-color="theme.colors.gray[900]"
          class="mb-5 h-11"
          item-class="font-sans !text-xl !font-medium !text-gray-600"
          @click="backToSubmenu"
        />
        <ul class="flex h-full flex-col gap-y-3 p-4" @click="handleClick">
          <SFLink
            v-for="child in selectedSubItem.children"
            :key="child.id"
            :to="buildNavigationTreeItemRoute(child)?.route || ''"
            class="flex min-h-[88px] w-full cursor-pointer items-center rounded-md bg-gray-200 p-4"
            @click="$emit('clickLink')"
          >
            <SFNavigationTreeItem
              :navigation-item="child"
              :text-color="theme.colors.gray[900]"
              class="!pb-0 font-sans text-lg !font-normal !text-gray-600"
              show-navigation-image
            />
          </SFLink>
        </ul>
      </MobileNavPanel>
    </TransitionGroup>
  </div>
</template>
<script lang="ts" setup>
import type { Ref } from 'vue'
import { nextTick, ref, watch } from 'vue'
import type {
  NavigationItems,
  NavigationTreeItem as NavigationTreeItemType,
} from '@scayle/storefront-nuxt'
import MobileNavPanel from './MobileNavPanel.vue'
import SFMobileNavigationBackButton from './SFMobileNavigationBackButton.vue'
import { SFButton, SFLink } from '#storefront-ui/components'
import SFNavigationTreeItem from '~/components/SFNavigationTreeItem.vue'
import { theme } from '#tailwind-config'
import { useRouteHelpers } from '~/composables'
import SFMobileHeaderUserArea from '~/components/layout/headers/SFMobileHeaderUserArea.vue'
import { useDefaultBreakpoints } from '~/modules/ui/runtime/composables/useDefaultBreakpoints'

const { isOpen, navigationItems } = defineProps<{
  isOpen: boolean
  navigationItems?: NavigationItems
}>()

const emit = defineEmits<{ clickLink: []; scrollToTop: [] }>()

const { buildNavigationTreeItemRoute } = useRouteHelpers()

const selectedItem = ref<NavigationTreeItemType | undefined>(undefined)
const selectedSubItem = ref<NavigationTreeItemType | undefined>(undefined)
const direction = ref<'forward' | 'backward'>('forward')
const navContainer = ref<HTMLElement | null>(null)

const scrollToTop = () => {
  emit('scrollToTop')
}

function selectNavigationLevel(
  item: NavigationTreeItemType,
  selectedRef: Ref<NavigationTreeItemType | undefined>,
  resetRef?: Ref<NavigationTreeItemType | undefined>,
) {
  if (item.children.length === 0) {
    emit('clickLink')
    return
  }
  direction.value = 'forward'
  selectedRef.value = item
  if (resetRef) {
    resetRef.value = undefined
  }
}

const { smaller } = useDefaultBreakpoints()
const isMobile = smaller('md')

const handleClick = () => {
  if (isMobile) {
    emit('clickLink')
  }
}

const selectItem = (item: NavigationTreeItemType) =>
  selectNavigationLevel(item, selectedItem, selectedSubItem)

const selectSubItem = (item: NavigationTreeItemType) =>
  selectNavigationLevel(item, selectedSubItem)

const backToMain = () => {
  direction.value = 'backward'
  selectedItem.value = undefined
  selectedSubItem.value = undefined
}
const backToSubmenu = () => {
  direction.value = 'backward'
  selectedSubItem.value = undefined
}

watch(
  () => isOpen,
  () => {
    selectedItem.value = undefined
    selectedSubItem.value = undefined
    direction.value = 'forward'
  },
)

watch(
  [selectedItem, selectedSubItem],
  ([newItem, newSubItem], [oldItem, oldSubItem]) => {
    if (newItem !== oldItem || newSubItem !== oldSubItem) {
      scrollToTop()
    }
  },
)

// Custom transition logic for sliding panels
const beforeEnter = (el: HTMLElement) => {
  el.style.transform =
    direction.value === 'forward' ? 'translateX(100%)' : 'translateX(-100%)'
  el.style.transition = 'none'
}
const enter = (el: HTMLElement, done: () => void) => {
  nextTick(() => {
    el.style.transition = 'transform 0.2s ease-in-out'
    el.style.transform = 'translateX(0)'
    el.addEventListener('transitionend', done, { once: true })
  })
}
const leave = (el: HTMLElement, done: () => void) => {
  el.classList.add('mobile-nav-leave-absolute')

  nextTick(() => {
    el.style.transition = 'transform 0.2s ease-in-out'
    el.style.transform =
      direction.value === 'forward' ? 'translateX(-100%)' : 'translateX(100%)'
    el.addEventListener('transitionend', done, { once: true })
  })
}
const afterLeave = (el: HTMLElement) => {
  el.classList.remove('mobile-nav-leave-absolute')
}
</script>
